import React, { useState, useRef } from 'react';
import useStyles from './useStyles';
import PropTypes from 'prop-types';
import {
  Button,
  IconButton,
  Popper,
  TextareaAutosize,
  CircularProgress,
  ClickAwayListener,
  ButtonGroup
} from '@mui/material';
import { TemplatePicker, TagPicker, SignatureToggle } from './components';
import DayPickerInput from 'react-day-picker/DayPickerInput';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import TimeField from 'react-simple-timefield';
import { formatDate } from 'react-day-picker/moment';

function ChatBox({
  conversation,
  userOptions,
  body,
  setBody,
  signature,
  setSignature,
  handleSend,
  sending,
  scheduleDate,
  setScheduleDate,
  scheduleTime,
  setScheduleTime,
  isPm,
  setIsPm,
  job
}) {
  const [isSchedulerOpen, setIsSchedulerOpen] = useState(false);
  const timeInputEl = useRef(null);
  const anchor = useRef(null);
  const textArea = useRef(null);

  const classes = useStyles();

  function insertTag(tag) {
    const index = textArea.current.selectionStart;
    setBody(body.slice(0, index) + tag + body.substring(index));
  }

  function handleTimeChange(event, value) {
    const time = value.replace(/-/g, ':');
    const [hour, minute] = time.split(':');
    hour >= 12 ? setScheduleTime(`12:${minute}:00`) : setScheduleTime(`${hour}:${minute}:00`);
  }

  return (
    <div className={classes.root}>
      <TextareaAutosize
        ref={textArea}
        className={classes.textArea}
        minRows={2}
        maxRows={6}
        placeholder={`Reply to ${conversation.length > 1 ? `${conversation.length} applicants` : conversation[0].candidate_name}`}
        value={body}
        onChange={(e) => setBody(e.target.value)}
      />
      {signature && <div className={classes.signature}>{signature}</div>}
      <div className={classes.actions}>
        <div className={classes.actionButtons}>
          <TagPicker insertTag={insertTag} job={job} />
          <TemplatePicker setBody={setBody} userOptions={userOptions} />
          {userOptions && userOptions.signature && (
            <SignatureToggle
              signature={signature}
              setSignature={setSignature}
              userOptions={userOptions}
            />
          )}
        </div>
        <div className={classes.characterCount}>
          {body.length + signature.length}/420 characters
        </div>
        <div className={classes.send}>
          <Button
            disableElevation
            variant="contained"
            classes={{ root: classes.sendButton }}
            onClick={handleSend}
          >
            {sending ? (
              <CircularProgress thickness={4.5} size={28} classes={{ root: classes.spinner }} />
            ) : scheduleDate ? (
              'Schedule'
            ) : (
              'Send'
            )}
          </Button>
          <ClickAwayListener onClickAway={() => setIsSchedulerOpen(false)}>
            <div>
              <IconButton
                classes={{
                  root: `${classes.schedulerButton} ${isSchedulerOpen ? classes.schedulerButtonActive : ''}`
                }}
                ref={anchor}
                onClick={() => setIsSchedulerOpen(!isSchedulerOpen)}
              >
                <ArrowDropDownIcon fontSize="small" />
              </IconButton>
              <Popper
                className={`${classes.popper} ${classes.popperScheduler}`}
                anchorEl={anchor.current}
                open={isSchedulerOpen}
                placement="top-end"
                popperOptions={{ positionFixed: true }}
              >
                <h4>Schedule message to be sent</h4>
                <div className={classes.schedulerOptions}>
                  <DayPickerInput
                    value={scheduleDate}
                    onDayChange={(day) => setScheduleDate(formatDate(day, 'LL'))}
                    classNames={{
                      container: classes.calendarContainer
                    }}
                    format="LL"
                    placeholder="Select a date"
                  />
                  <div className={classes.startingTimeInputWrapper}>
                    <TimeField
                      ref={timeInputEl}
                      value={scheduleTime}
                      onChange={handleTimeChange}
                      className={classes.startTimeInput}
                    />
                    <ButtonGroup className={classes.timeBntGroupWrapper}>
                      <Button
                        className={!isPm && classes.isAMActive}
                        onClick={() => setIsPm(false)}
                      >
                        AM
                      </Button>
                      <Button className={isPm && classes.isPMActive} onClick={() => setIsPm(true)}>
                        PM
                      </Button>
                    </ButtonGroup>
                    {scheduleDate && (
                      <IconButton
                        onClick={() => {
                          setScheduleDate('');
                          setScheduleTime('00:00');
                        }}
                      >
                        <HighlightOffIcon color="error" />
                      </IconButton>
                    )}
                  </div>
                </div>
              </Popper>
            </div>
          </ClickAwayListener>
        </div>
      </div>
    </div>
  );
}

ChatBox.propTypes = {
  conversation: PropTypes.array.isRequired,
  userOptions: PropTypes.object.isRequired,
  body: PropTypes.string,
  setBody: PropTypes.func.isRequired,
  signature: PropTypes.string,
  setSignature: PropTypes.func.isRequired,
  handleSend: PropTypes.func.isRequired,
  sending: PropTypes.bool.isRequired,
  scheduleDate: PropTypes.string.isRequired,
  setScheduleDate: PropTypes.func.isRequired,
  scheduleTime: PropTypes.string.isRequired,
  setScheduleTime: PropTypes.func.isRequired,
  isPm: PropTypes.bool.isRequired,
  setIsPm: PropTypes.func.isRequired,
  job: PropTypes.object.isRequired
};

export default ChatBox;
